import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { urlFile } from '../../../AxiosConfig';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { normalizeSearch } from '../../recorrencias/helpers';
import { ViewEstabInfoModal } from '../componentes/ViewEstabInfoModal';
import { EstabelecimentosService } from '../system-admin/Service';
import { ChevronRight, Search, Store, TriangleAlert } from 'lucide-react';

const estabsService = new EstabelecimentosService();
const MAX_VISIBLE = 50;

export const EstabsClientAddFirst = () => {
    const navigate = useNavigate();

    return (
        <div className="sf-state">
            <Store aria-hidden />
            <h2>Nenhum estabelecimento vinculado</h2>
            <p>Adicione o estabelecimento onde você é atendido para agendar horários.</p>
            <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/meus-estabelecimentos?action=add')}
            >
                Adicionar estabelecimento
            </button>
        </div>
    );
};

export const UserClientEstabAdd = () => {
    const { estabsUser } = useEstabsContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [estabs, setEstabs] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState('');

    const loadEstabs = async () => {
        try {
            setLoading(true);
            setError(false);
            const { data } = await estabsService.findAll();
            setEstabs(Array.isArray(data) ? data : []);
        } catch (e) {
            console.log(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEstabs();
    }, []);

    const filtered = useMemo(() => {
        const term = normalizeSearch(search);
        if (!term) return estabs;

        return estabs.filter((estab) =>
            normalizeSearch(`${estab.name} ${estab.address?.city || ''} ${estab.address?.neighborhood || ''}`).includes(term),
        );
    }, [estabs, search]);

    if (loading) {
        return (
            <div className="mx-auto max-w-2xl space-y-2 px-2" aria-busy="true">
                <div className="sf-skeleton h-10" />
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os estabelecimentos</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={loadEstabs}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    return (
        <div className="sf-page mx-auto max-w-2xl px-2">
            <div className="sf-toolbar-row">
                <label className="sf-search !max-w-none">
                    <Search aria-hidden />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nome, cidade ou bairro"
                        aria-label="Buscar estabelecimento"
                    />
                </label>
            </div>

            {filtered.length === 0 ? (
                <div className="sf-state">
                    <Search aria-hidden />
                    <h2>Nenhum estabelecimento encontrado</h2>
                </div>
            ) : (
                <ul className="sf-group [--sf-row-inset:4.75rem]">
                    {filtered.slice(0, MAX_VISIBLE).map((estab) => {
                        const linked = estabsUser.some((item) => item._id === estab._id && item.type === 'client');
                        const location = [estab.address?.neighborhood, estab.address?.city].filter(Boolean).join(', ');

                        return (
                            <li key={estab._id}>
                                <button type="button" className="sf-row" onClick={() => setOpenId(estab._id)}>
                                    <span className="sf-thumb">
                                        {estab.imageId ? (
                                            <img src={`${urlFile}/${estab.imageId}`} alt="" className="!object-contain p-1" />
                                        ) : (
                                            <Store aria-hidden />
                                        )}
                                    </span>
                                    <span className="sf-row-body">
                                        <span className="sf-row-title">{estab.name}</span>
                                        {!!location && <span className="sf-row-sub">{location}</span>}
                                    </span>
                                    {linked && (
                                        <span className="sf-tag shrink-0" data-tone="green">
                                            Vinculado
                                        </span>
                                    )}
                                    <ChevronRight className="sf-chevron" aria-hidden />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {filtered.length > MAX_VISIBLE && (
                <p className="mt-3 text-center text-xs text-base-content/55">
                    Mostrando {MAX_VISIBLE} de {filtered.length}. Use a busca para encontrar o seu.
                </p>
            )}

            {!!openId && <ViewEstabInfoModal estabId={openId} onClose={() => setOpenId('')} />}
        </div>
    );
};
